import { queueMacrotask } from 'module/helpers/macrotask';
import { useTranslation } from 'module/hooks/use-translation';
import { Form } from 'module/ui/components/atoms/form';
import { Button, InputField } from '@rocketlane/ui';
import { NodeViewContent } from '@tiptap/react';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { SharedConfigurationBar } from 'components/organisms/document-editor/extensions/custom-section/ui/shared/configuration-bar';
import { SectionHeading } from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-heading';
import {
  SectionCard,
  SectionContent,
  SectionDivider,
} from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-styles';
import { ConfigurationBar } from '../../../shared/section-styles';

const StyledForm = styled(Form)`
  margin-bottom: var(--spacing-05);
`;

export const PromptInput = ({
  prompt,
  setPrompt,
  onGenerate,
  onCancel,
  onUndo,
  allowResolve,
  getNodeAsPayload,
  pageType,
}) => {
  const [form] = Form.useForm<{
    title: string;
    question: string;
    aiContextChecked: boolean;
    aiContext: string;
  }>();
  const [showNext, setShowNext] = useState(!prompt.title?.trim());
  const titlePlaceholder = useTranslation(
    'rich_text_editor.sections.extract_info_section.table_title_placeholder',
  );
  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    form.setFieldsValue({
      title: prompt.title,
    });
  }, [
    form,
    prompt.aiContext,
    prompt.aiContextChecked,
    prompt.question,
    prompt.title,
  ]);

  useEffect(() => {
    queueMacrotask(() => {
      titleRef.current?.select();
    });
  }, []);

  const triggerGenerate = () => {
    const promptHash = {
      ...prompt,
      title: form.getFieldValue('title'),
    };

    setPrompt(promptHash);
    onGenerate(promptHash);
  };

  const isDisabled = !prompt.title?.trim();

  return (
    <SectionCard>
      <SectionHeading
        title="editor.sections.table_details_section.title"
        onClose={onUndo}
        showCloseButton={true}
      />
      <SectionContent>
        <StyledForm form={form} layout="vertical">
          <Form.Item
            name="title"
            label={
              <FormattedMessage id="rich_text_editor.sections.question_section.title" />
            }
          >
            <InputField
              type="text"
              ref={titleRef}
              onChange={(e) => {
                setPrompt({
                  ...prompt,
                  title: e.target.value,
                });
              }}
              placeholder={titlePlaceholder}
              autoFocus
            />
          </Form.Item>

          {!showNext && <NodeViewContent />}
        </StyledForm>
        {showNext ? (
          <ConfigurationBar>
            <Button
              variant="primary"
              disabled={!prompt.title?.trim()}
              onClick={() => {
                setShowNext(false);
              }}
            >
              <FormattedMessage id="next" />
            </Button>
          </ConfigurationBar>
        ) : (
          <>
            <SectionDivider />
            <SharedConfigurationBar
              prompt={prompt}
              setPrompt={setPrompt}
              onCancel={onCancel}
              onSubmit={triggerGenerate}
              validation={{
                isDisabled,
              }}
              getNodeAsPayload={getNodeAsPayload}
              allowResolve={allowResolve}
              actionBarConfig={{
                canShowPreviewButton: false,
                canWrapTrashButton: true,
                canShowStreaming: true,
              }}
              pageType={pageType}
            />
          </>
        )}
      </SectionContent>
    </SectionCard>
  );
};
